import { supabase } from '../lib/supabaseClient';

/**
 * Acceso al esquema `academia` (0064) desde el panel.
 *
 * ⚠️ Es un esquema DISTINTO de `public`: todo pasa por `db()`. PostgREST solo lo alcanza
 * porque `academia` está declarado en Settings → API → Exposed schemas del proyecto; sin
 * eso, cada consulta de este archivo responde `PGRST106: Invalid schema`.
 *
 * ⚠️ `opciones.es_correcta` NO se lee ni se escribe por la tabla: la 0065 le quitó ese
 * permiso a `authenticated` para que la respuesta no viaje al navegador de quien rinde.
 * El editor la lee por `opciones_para_editar()` y la marca por `marcar_opcion_correcta()`.
 */
const db = () => supabase.schema('academia');

export type EstadoCurso = 'borrador' | 'publicado' | 'proximamente' | 'archivado';

export interface EspacioRow {
  id: string;
  slug: string;
  nombre: string;
  cliente: string;
  activo: boolean;
}

export interface CursoRow {
  id: string;
  espacio_id: string;
  slug: string;
  titulo: string;
  descripcion: string;
  emoji: string;
  etiqueta: string;
  gradiente_1: string;
  gradiente_2: string;
  duracion_min: number;
  orden: number;
  estado: EstadoCurso;
  nota_minima: number;
  insignia_ico: string;
  insignia_nombre: string;
  lecciones_previstas: number | null;
  preguntas_previstas: number | null;
}

export interface LeccionRow {
  id: string;
  curso_id: string;
  titulo: string;
  orden: number;
}

export interface BloqueRow {
  id: string;
  leccion_id: string;
  orden: number;
  tipo: string;
  contenido: Record<string, unknown>;
}

export interface PreguntaRow {
  id: string;
  curso_id: string;
  enunciado: string;
  explicacion: string;
  orden: number;
}

/** Con `es_correcta`: solo llega por `opciones_para_editar`, nunca por la tabla. */
export interface OpcionEditable {
  id: string;
  pregunta_id: string;
  texto: string;
  orden: number;
  es_correcta: boolean;
}

export interface ResultadoRecurso {
  worker_id: string;
  dni: string;
  nombre: string;
  cursos_publicados: number;
  cursos_aprobados: number;
  en_curso: number;
  sin_empezar: number;
  nota_promedio: number | null;
  puntos: number;
  ultima_actividad: string | null;
}

// ---------------------------------------------------------------------------
// Espacios y cursos
// ---------------------------------------------------------------------------

export async function listarEspacios(): Promise<EspacioRow[]> {
  const { data, error } = await db()
    .from('espacios')
    .select('id, slug, nombre, cliente, activo')
    .order('nombre');
  if (error) throw error;
  return (data ?? []) as EspacioRow[];
}

/** Todos los estados, incluido `borrador`: el editor tiene que ver lo que aún no se publicó. */
export async function listarCursos(espacioId: string): Promise<CursoRow[]> {
  const { data, error } = await db()
    .from('cursos')
    .select('*')
    .eq('espacio_id', espacioId)
    .order('orden');
  if (error) throw error;
  return (data ?? []) as CursoRow[];
}

export async function obtenerCurso(cursoId: string): Promise<CursoRow | null> {
  const { data, error } = await db().from('cursos').select('*').eq('id', cursoId).maybeSingle();
  if (error) throw error;
  return (data ?? null) as CursoRow | null;
}

export async function crearCurso(
  espacioId: string,
  datos: { titulo: string; slug: string; descripcion: string; emoji: string; duracionMin: number },
): Promise<CursoRow> {
  // Nace en `borrador` SIEMPRE: un curso a medias que aparece publicado es peor que uno
  // que no existe, porque alguien lo empieza y se queda a la mitad.
  const { data, error } = await db()
    .from('cursos')
    .insert({
      espacio_id: espacioId,
      slug: datos.slug,
      titulo: datos.titulo,
      descripcion: datos.descripcion,
      emoji: datos.emoji,
      duracion_min: datos.duracionMin,
      estado: 'borrador',
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as CursoRow;
}

export async function actualizarCurso(
  cursoId: string,
  cambios: Partial<Omit<CursoRow, 'id' | 'espacio_id'>>,
): Promise<void> {
  const { error } = await db().from('cursos').update(cambios).eq('id', cursoId);
  if (error) throw error;
}

export async function cambiarEstadoCurso(cursoId: string, estado: EstadoCurso): Promise<void> {
  const cambios: Record<string, unknown> = { estado };
  // `publicado_at` marca CUÁNDO se publicó, que es lo que se responde si alguien pregunta
  // desde cuándo estaba disponible un curso.
  if (estado === 'publicado') cambios.publicado_at = new Date().toISOString();
  const { error } = await db().from('cursos').update(cambios).eq('id', cursoId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Lecciones y bloques
// ---------------------------------------------------------------------------

export async function listarLecciones(cursoId: string): Promise<LeccionRow[]> {
  const { data, error } = await db()
    .from('lecciones')
    .select('id, curso_id, titulo, orden')
    .eq('curso_id', cursoId)
    .order('orden');
  if (error) throw error;
  return (data ?? []) as LeccionRow[];
}

export async function crearLeccion(cursoId: string, titulo: string, orden: number) {
  const { error } = await db().from('lecciones').insert({ curso_id: cursoId, titulo, orden });
  if (error) throw error;
}

export async function actualizarLeccion(id: string, cambios: { titulo?: string; orden?: number }) {
  const { error } = await db().from('lecciones').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function borrarLeccion(id: string) {
  // Los bloques caen con ella por `on delete cascade`. El AVANCE no: cuelga de
  // `inscripciones`, así que borrar una lección no le quita el curso a nadie.
  const { error } = await db().from('lecciones').delete().eq('id', id);
  if (error) throw error;
}

export async function listarBloques(leccionId: string): Promise<BloqueRow[]> {
  const { data, error } = await db()
    .from('bloques')
    .select('id, leccion_id, orden, tipo, contenido')
    .eq('leccion_id', leccionId)
    .order('orden');
  if (error) throw error;
  return (data ?? []) as BloqueRow[];
}

export async function crearBloque(
  leccionId: string,
  tipo: string,
  contenido: Record<string, unknown>,
  orden: number,
) {
  const { error } = await db()
    .from('bloques')
    .insert({ leccion_id: leccionId, tipo, contenido, orden });
  if (error) throw error;
}

export async function actualizarBloque(
  id: string,
  cambios: { tipo?: string; contenido?: Record<string, unknown>; orden?: number },
) {
  const { error } = await db().from('bloques').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function borrarBloque(id: string) {
  const { error } = await db().from('bloques').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Preguntas y alternativas
// ---------------------------------------------------------------------------

export async function listarPreguntas(cursoId: string): Promise<PreguntaRow[]> {
  const { data, error } = await db()
    .from('preguntas')
    .select('id, curso_id, enunciado, explicacion, orden')
    .eq('curso_id', cursoId)
    .order('orden');
  if (error) throw error;
  return (data ?? []) as PreguntaRow[];
}

/**
 * Las alternativas CON su respuesta correcta.
 *
 * Va por función y no por tabla porque `es_correcta` está revocada para `authenticated`
 * (0065). La función comprueba el permiso de edición del espacio antes de devolverla.
 */
export async function listarOpcionesEditables(cursoId: string): Promise<OpcionEditable[]> {
  const { data, error } = await db().rpc('opciones_para_editar', { p_curso_id: cursoId });
  if (error) throw error;
  return (data ?? []) as OpcionEditable[];
}

export async function crearPregunta(cursoId: string, enunciado: string, orden: number) {
  const { data, error } = await db()
    .from('preguntas')
    .insert({ curso_id: cursoId, enunciado, orden })
    .select('id')
    .single();
  if (error) throw error;
  return data.id as string;
}

export async function actualizarPregunta(
  id: string,
  cambios: { enunciado?: string; explicacion?: string; orden?: number },
) {
  const { error } = await db().from('preguntas').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function borrarPregunta(id: string) {
  const { error } = await db().from('preguntas').delete().eq('id', id);
  if (error) throw error;
}

export async function crearOpcion(preguntaId: string, texto: string, orden: number) {
  // Nace sin marcar: `es_correcta` no se puede escribir por la API (0065).
  const { error } = await db().from('opciones').insert({ pregunta_id: preguntaId, texto, orden });
  if (error) throw error;
}

export async function actualizarOpcion(id: string, cambios: { texto?: string; orden?: number }) {
  const { error } = await db().from('opciones').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function borrarOpcion(id: string) {
  const { error } = await db().from('opciones').delete().eq('id', id);
  if (error) throw error;
}

/**
 * El ÚNICO camino para marcar la alternativa correcta (0065).
 *
 * Apaga las demás de la misma pregunta en la misma transacción, así no puede quedar una
 * pregunta con dos correctas ni con ninguna.
 */
export async function marcarOpcionCorrecta(opcionId: string) {
  const { error } = await db().rpc('marcar_opcion_correcta', { p_opcion_id: opcionId });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Resultados
// ---------------------------------------------------------------------------

/**
 * Resultados por recurso: el cruce que motivó que la Academia comparta base.
 *
 * `v_resumen_recurso` está en `academia` y `profiles` en `public`, así que se piden por
 * separado y se unen acá: PostgREST no cruza esquemas en un embed. La vista es
 * `security_invoker`, así que cada quien ve solo lo que sus políticas le permiten.
 */
/** Una pregunta de un intento, ya resuelta por el servidor. */
export interface PreguntaDelIntento {
  orden: number;
  enunciado: string;
  explicacion: string;
  /** Lo que la persona marcó. */
  marco: string | null;
  /** Lo que era. */
  correcta: string | null;
  acerto: boolean;
}

export interface IntentoDetallado {
  nota: number;
  correctas: number;
  total: number;
  aprobado: boolean;
  terminado_at: string;
  preguntas: PreguntaDelIntento[];
}

export interface CursoDelRecurso {
  curso_id: string;
  titulo: string;
  nota_minima: number;
  inscrito: boolean;
  bloques_leidos: number;
  bloques_totales: number;
  /** Del más reciente al más antiguo. */
  intentos: IntentoDetallado[];
}

/**
 * El detalle de UNA persona: qué curso tomó, con qué nota y qué pregunta falló.
 *
 * Va por RPC y no por consultas sueltas porque necesita `opciones.es_correcta`, que está
 * revocada al navegador (0065). El cruce lo hace el servidor y devuelve el texto ya resuelto,
 * sin abrirle esa columna a nadie. Ver `academia.detalle_recurso` (0177).
 */
export async function obtenerDetalleRecurso(
  espacioId: string,
  workerId: string,
): Promise<CursoDelRecurso[]> {
  const { data, error } = await db().rpc('detalle_recurso', {
    p_espacio: espacioId,
    p_worker: workerId,
  });
  if (error) throw error;
  const payload = (data ?? {}) as { cursos?: CursoDelRecurso[]; error?: string };
  // El «sin permiso» viaja en el cuerpo, no como excepción: la fila se muestra igual y el
  // detalle dice por qué está vacío, en vez de reventar la pantalla entera.
  if (payload.error) throw new Error(payload.error);
  return payload.cursos ?? [];
}

export async function listarResultados(espacioId: string): Promise<ResultadoRecurso[]> {
  // Parte de `cobertura_espacio`, NO de `v_resumen_recurso`: esa sale de las tablas de
  // progreso, así que solo conoce a quien ya hizo algo. La pregunta que sirve —«¿a quién
  // hay que ir a buscar?»— necesita empezar por quién tiene acceso.
  const [{ data: cobertura, error }, { data: resumen }] = await Promise.all([
    db().rpc('cobertura_espacio', { p_espacio: espacioId }),
    db().from('v_resumen_recurso').select('worker_id, nota_promedio, puntos'),
  ]);
  if (error) throw error;

  const filas = (cobertura ?? []) as {
    worker_id: string;
    cursos_publicados: number;
    aprobados: number;
    en_curso: number;
    sin_empezar: number;
    ultima_actividad: string | null;
  }[];
  if (filas.length === 0) return [];

  // ⚠️ Nunca `select('*')` sobre profiles: las columnas bancarias están revocadas y la
  // consulta entera falla con 42501.
  const { data: personas, error: e2 } = await supabase
    .from('profiles')
    .select('id, dni, first_names, last_names')
    .in('id', filas.map((f) => f.worker_id));
  if (e2) throw e2;

  const porId = new Map((personas ?? []).map((p) => [p.id, p]));
  const puntosPorId = new Map(
    ((resumen ?? []) as { worker_id: string; nota_promedio: number | null; puntos: number }[]).map(
      (r) => [r.worker_id, r],
    ),
  );

  return filas
    .map((f) => {
      const p = porId.get(f.worker_id);
      const r = puntosPorId.get(f.worker_id);
      return {
        worker_id: f.worker_id,
        dni: p?.dni ?? '—',
        // Bajo RLS un perfil que no se puede ver simplemente no viene: se dice, no se
        // inventa un nombre (§3.7 de la bitácora).
        nombre: p ? `${p.first_names ?? ''} ${p.last_names ?? ''}`.trim() : 'Recurso no visible',
        cursos_publicados: f.cursos_publicados,
        cursos_aprobados: f.aprobados,
        en_curso: f.en_curso,
        sin_empezar: f.sin_empezar,
        nota_promedio: r?.nota_promedio ?? null,
        puntos: r?.puntos ?? 0,
        ultima_actividad: f.ultima_actividad,
      };
    })
    // Primero quien más debe: es la lista de a quién ir a buscar.
    .sort(
      (a, b) =>
        b.sin_empezar - a.sin_empezar ||
        b.en_curso - a.en_curso ||
        a.nombre.localeCompare(b.nombre),
    );
}

// ---------------------------------------------------------------------------
// Configuración y premios
// ---------------------------------------------------------------------------

export interface PremioRow {
  id: string;
  espacio_id: string;
  icono: string;
  nombre: string;
  detalle: string;
  puntos: number;
  stock: number | null;
  activo: boolean;
  orden: number;
}

export interface ConfigEspacio {
  notaMinima: number;
  bonusNotaPerfecta: number;
}

const CONFIG_POR_DEFECTO: ConfigEspacio = { notaMinima: 60, bonusNotaPerfecta: 50 };

/**
 * La config vive como filas `clave/valor` en `academia.config`, no como columnas.
 *
 * Es a propósito: agregar un parámetro nuevo no debe costar una migración. Lo que sí cuesta
 * es que el valor llegue como `jsonb`, así que se normaliza acá y no en cada pantalla.
 */
export async function obtenerConfig(espacioId: string): Promise<ConfigEspacio> {
  const { data, error } = await db()
    .from('config')
    .select('clave, valor')
    .eq('espacio_id', espacioId);
  if (error) throw error;

  const mapa = new Map((data ?? []).map((f) => [f.clave as string, f.valor]));
  const numero = (clave: string, porDefecto: number) => {
    const v = mapa.get(clave);
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : porDefecto;
  };

  return {
    notaMinima: numero('nota_minima', CONFIG_POR_DEFECTO.notaMinima),
    bonusNotaPerfecta: numero('bonus_nota_perfecta', CONFIG_POR_DEFECTO.bonusNotaPerfecta),
  };
}

export async function guardarConfig(espacioId: string, clave: string, valor: number) {
  const { error } = await db()
    .from('config')
    .upsert(
      { espacio_id: espacioId, clave, valor, updated_at: new Date().toISOString() },
      { onConflict: 'espacio_id,clave' },
    );
  if (error) throw error;
}

/** Todos, incluidos los desactivados: el editor tiene que poder volver a encenderlos. */
export async function listarPremios(espacioId: string): Promise<PremioRow[]> {
  const { data, error } = await db()
    .from('premios')
    .select('*')
    .eq('espacio_id', espacioId)
    .order('orden');
  if (error) throw error;
  return (data ?? []) as PremioRow[];
}

export async function crearPremio(espacioId: string, nombre: string, puntos: number, orden: number) {
  const { error } = await db()
    .from('premios')
    .insert({ espacio_id: espacioId, nombre, puntos, orden, icono: '🎁' });
  if (error) throw error;
}

export async function actualizarPremio(
  id: string,
  cambios: Partial<Omit<PremioRow, 'id' | 'espacio_id'>>,
) {
  const { error } = await db().from('premios').update(cambios).eq('id', id);
  if (error) throw error;
}

export async function borrarPremio(id: string) {
  // `canjes.premio_id` es `on delete restrict`: un premio ya canjeado NO se puede borrar,
  // porque dejaría un canje apuntando al vacío. Se desactiva en su lugar.
  const { error } = await db().from('premios').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Canjes
// ---------------------------------------------------------------------------

export interface CanjeRow {
  id: string;
  worker_id: string;
  premio_id: string;
  puntos_gastados: number;
  estado: 'solicitado' | 'aprobado' | 'rechazado';
  nota: string | null;
  solicitado_at: string;
  premio_nombre: string;
  premio_icono: string;
  worker_nombre: string;
  worker_dni: string;
}

/** Los canjes pendientes de resolver, con el nombre de quien los pidió. */
export async function listarCanjes(estado?: CanjeRow['estado']): Promise<CanjeRow[]> {
  let consulta = db()
    .from('canjes')
    .select('id, worker_id, premio_id, puntos_gastados, estado, nota, solicitado_at, premios ( nombre, icono )')
    .order('solicitado_at', { ascending: false });
  if (estado) consulta = consulta.eq('estado', estado);

  const { data, error } = await consulta;
  if (error) throw error;
  const filas = data ?? [];
  if (filas.length === 0) return [];

  // `profiles` está en `public`: PostgREST no cruza esquemas en un embed, y nunca `select('*')`.
  const { data: personas, error: e2 } = await supabase
    .from('profiles')
    .select('id, dni, first_names, last_names')
    .in('id', [...new Set(filas.map((f) => f.worker_id))]);
  if (e2) throw e2;

  const porId = new Map((personas ?? []).map((p) => [p.id, p]));
  return filas.map((f) => {
    const p = porId.get(f.worker_id);
    const premio = (f.premios ?? {}) as { nombre?: string; icono?: string };
    return {
      id: f.id,
      worker_id: f.worker_id,
      premio_id: f.premio_id,
      puntos_gastados: f.puntos_gastados,
      estado: f.estado,
      nota: f.nota,
      solicitado_at: f.solicitado_at,
      premio_nombre: premio.nombre ?? '—',
      premio_icono: premio.icono ?? '🎁',
      worker_nombre: p ? `${p.first_names ?? ''} ${p.last_names ?? ''}`.trim() : 'Recurso no visible',
      worker_dni: p?.dni ?? '—',
    };
  });
}

/**
 * Aprobar o rechazar. Los puntos se descuentan AL APROBAR, dentro de la función: un canje
 * rechazado no le cuesta nada a nadie y el saldo sigue siendo la suma del libro mayor.
 */
export async function resolverCanje(
  canjeId: string,
  estado: 'aprobado' | 'rechazado',
  nota?: string,
) {
  const { error } = await db().rpc('resolver_canje', {
    p_canje_id: canjeId,
    p_estado: estado,
    p_nota: nota ?? null,
  });
  if (error) throw error;
}
